import { useEffect, useMemo, useState } from "react";
import { Copy, ExternalLink, Image as ImageIcon, Music, Plus, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { getPageBaseUrl } from "@/routes/pages/edit/pageEditorUtils";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ImageSelectDialog } from "@/components/ImageSelectDialog";
import { AudioSelectDialog } from "@/components/AudioSelectDialog";
import {
  PLATFORM_ICON_FILENAMES,
  platformIconUrl,
  type PlatformIconFilename,
} from "@/lib/platformIcons";
import type {
  ProductEditAudioFile,
  ProductEditImage,
  ProductEditLink,
} from "@/routes/productEditTypes";

const PLATFORM_ICON_DEFAULTS: Record<
  PlatformIconFilename,
  { label: string; ctaText: string; urlPlaceholder: string }
> = {
  "amazon-music.png": {
    label: "Amazon Music",
    ctaText: "Listen",
    urlPlaceholder: "https://music.amazon.com/...",
  },
  "anghami.png": {
    label: "Anghami",
    ctaText: "Listen",
    urlPlaceholder: "https://anghami.com/...",
  },
  "apple-music.png": {
    label: "Apple Music",
    ctaText: "Listen",
    urlPlaceholder: "https://music.apple.com/...",
  },
  "audiomack.png": {
    label: "Audiomack",
    ctaText: "Listen",
    urlPlaceholder: "https://audiomack.com/...",
  },
  "bandcamp.png": {
    label: "Bandcamp",
    ctaText: "Listen",
    urlPlaceholder: "https://artist.bandcamp.com/...",
  },
  "boomplay.png": {
    label: "Boomplay",
    ctaText: "Listen",
    urlPlaceholder: "https://www.boomplay.com/...",
  },
  "deezer.png": {
    label: "Deezer",
    ctaText: "Listen",
    urlPlaceholder: "https://www.deezer.com/...",
  },
  "iheart-radio.png": {
    label: "iHeartRadio",
    ctaText: "Listen",
    urlPlaceholder: "https://www.iheart.com/...",
  },
  "joox.png": {
    label: "JOOX",
    ctaText: "Listen",
    urlPlaceholder: "https://www.joox.com/...",
  },
  "kkbox.png": {
    label: "KKBOX",
    ctaText: "Listen",
    urlPlaceholder: "https://www.kkbox.com/...",
  },
  "multiplatform.png": {
    label: "Multiplatform",
    ctaText: "Listen",
    urlPlaceholder: "https://...",
  },
  "napster.png": {
    label: "Napster",
    ctaText: "Listen",
    urlPlaceholder: "https://napster.com/...",
  },
  "netease-cloud-music.png": {
    label: "NetEase Cloud Music",
    ctaText: "Listen",
    urlPlaceholder: "https://music.163.com/...",
  },
  "pandora.png": {
    label: "Pandora",
    ctaText: "Listen",
    urlPlaceholder: "https://www.pandora.com/...",
  },
  "qobuz.png": {
    label: "Qobuz",
    ctaText: "Listen",
    urlPlaceholder: "https://www.qobuz.com/...",
  },
  "soundcloud.png": {
    label: "SoundCloud",
    ctaText: "Listen",
    urlPlaceholder: "https://soundcloud.com/...",
  },
  "spotify.png": {
    label: "Spotify",
    ctaText: "Listen",
    urlPlaceholder: "https://open.spotify.com/...",
  },
  "tidal.png": {
    label: "TIDAL",
    ctaText: "Listen",
    urlPlaceholder: "https://tidal.com/...",
  },
  "yandex-music.png": {
    label: "Yandex Music",
    ctaText: "Listen",
    urlPlaceholder: "https://music.yandex.com/...",
  },
  "youtube-music.png": {
    label: "YouTube Music",
    ctaText: "Listen",
    urlPlaceholder: "https://music.youtube.com/...",
  },
};

function getPlatformIconDefaults(icon: string) {
  return PLATFORM_ICON_DEFAULTS[icon as PlatformIconFilename];
}

function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (icon: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-12 w-12 shrink-0 p-1"
          aria-label={value ? "Change icon" : "Select icon"}
        >
          {value ? (
            <img
              src={platformIconUrl({ icon: value })}
              alt=""
              className="h-full w-full object-contain"
            />
          ) : (
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <button
          type="button"
          className={`mb-2 flex h-10 w-full items-center justify-center gap-2 rounded-md border text-sm hover:bg-accent ${
            !value ? "border-primary ring-1 ring-primary" : "border-border"
          }`}
          onClick={() => {
            onChange("");
            setOpen(false);
          }}
        >
          <ImageIcon className="h-4 w-4 text-muted-foreground" />
          No icon
        </button>
        <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
          {PLATFORM_ICON_FILENAMES.map((icon) => (
            <button
              key={icon}
              type="button"
              className={`flex h-12 w-full items-center justify-center rounded-md border p-1 hover:bg-accent ${
                value === icon ? "border-primary ring-1 ring-primary" : "border-border"
              }`}
              onClick={() => {
                onChange(icon);
                setOpen(false);
              }}
              aria-label={icon.replace(/\.png$/, "").replace(/-/g, " ")}
            >
              <img
                src={platformIconUrl({ icon })}
                alt=""
                className="h-full w-full object-contain"
              />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LinkEditor({
  link,
  onChange,
  onRemove,
}: {
  link: ProductEditLink;
  onChange: (link: ProductEditLink) => void;
  onRemove: () => void;
}) {
  const iconDefaults = getPlatformIconDefaults(link.icon);

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-start gap-3">
        <IconPicker
          value={link.icon}
          onChange={(icon) => {
            if (!icon) {
              onChange({ ...link, icon: "" });
              return;
            }
            const defaults = getPlatformIconDefaults(icon);
            onChange({
              ...link,
              icon,
              label: defaults?.label ?? link.label,
              ctaText: defaults?.ctaText ?? link.ctaText,
            });
          }}
        />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`link-label-${link.icon}`}>Label</Label>
            <Input
              id={`link-label-${link.icon}`}
              value={link.label}
              onChange={(e) => onChange({ ...link, label: e.target.value })}
              placeholder={iconDefaults?.label ?? "Platform name"}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`link-cta-${link.icon}`}>CTA text</Label>
            <Input
              id={`link-cta-${link.icon}`}
              value={link.ctaText ?? ""}
              onChange={(e) => onChange({ ...link, ctaText: e.target.value })}
              placeholder={iconDefaults?.ctaText ?? "Listen"}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`link-url-${link.icon}`}>URL</Label>
            <Input
              id={`link-url-${link.icon}`}
              type="url"
              value={link.url}
              onChange={(e) => onChange({ ...link, url: e.target.value })}
              placeholder={iconDefaults?.urlPlaceholder ?? "https://..."}
            />
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 h-8 w-8 text-destructive hover:text-destructive"
          onClick={onRemove}
          aria-label="Remove link"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function ProductEditTrackTab({
  slug,
  onSlugChange,
  coverArt,
  onCoverArtChange,
  audioFiles,
  onAudioFilesChange,
  isrc,
  onIsrcChange,
  links,
  onLinksChange,
  buyDigitalEnabled,
  onBuyDigitalEnabledChange,
  buyDigitalText,
  onBuyDigitalTextChange,
}: {
  slug: string;
  onSlugChange: (slug: string) => void;
  coverArt: ProductEditImage | null;
  onCoverArtChange: (coverArt: ProductEditImage | null) => void;
  audioFiles: ProductEditAudioFile[];
  onAudioFilesChange: (audioFiles: ProductEditAudioFile[]) => void;
  isrc: string;
  onIsrcChange: (isrc: string) => void;
  links: ProductEditLink[];
  onLinksChange: (links: ProductEditLink[]) => void;
  buyDigitalEnabled: boolean;
  onBuyDigitalEnabledChange: (enabled: boolean) => void;
  buyDigitalText: string;
  onBuyDigitalTextChange: (text: string) => void;
}) {
  const [isCoverArtDialogOpen, setIsCoverArtDialogOpen] = useState(false);
  const [isAudioDialogOpen, setIsAudioDialogOpen] = useState(false);
  const [isRefreshingLinks, setIsRefreshingLinks] = useState(false);
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/custom-domains")
      .then((res) => res.json())
      .then((data) => {
        const domains = data.customDomains || [];
        setCustomDomainHostname(domains[0]?.hostname ?? null);
      })
      .catch(() => {});
  }, []);

  const pageUrl = useMemo(() => {
    const normalizedSlug = slug.trim().replace(/^\/+|\/+$/g, "");
    if (!normalizedSlug) return "";
    return `${getPageBaseUrl(customDomainHostname)}/${normalizedSlug}`;
  }, [customDomainHostname, slug]);

  const handleCopyPageUrl = async () => {
    if (!pageUrl) return;
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success("Copied to clipboard");
    } catch (error) {
      console.error("Failed to copy page URL:", error);
      toast.error("Failed to copy URL");
    }
  };

  const audioFile = audioFiles[0] ?? null;

  const updateLink = (index: number, link: ProductEditLink) => {
    onLinksChange(links.map((item, i) => (i === index ? link : item)));
  };

  const removeLink = (index: number) => {
    onLinksChange(links.filter((_, i) => i !== index));
  };

  const handleRefreshLinks = async () => {
    const trimmedIsrc = isrc.trim();
    if (!trimmedIsrc) {
      return;
    }

    setIsRefreshingLinks(true);
    try {
      const response = await fetch(
        `/api/songstats/track-links?isrc=${encodeURIComponent(trimmedIsrc)}`,
      );
      const data = (await response.json()) as {
        links?: ProductEditLink[];
        error?: string;
      };

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch links");
      }

      onLinksChange(data.links ?? []);
      toast.success("Links refreshed from ISRC");
    } catch (error) {
      console.error("Failed to refresh links from SongStats:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to refresh links",
      );
    } finally {
      setIsRefreshingLinks(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 max-w-md">
        <Label htmlFor="product-slug">URL slug</Label>
        <div className="flex gap-2">
          <Input
            id="product-slug"
            value={slug}
            onChange={(e) => onSlugChange(e.target.value)}
            placeholder="my-track"
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label="Copy page URL"
            title="Copy page URL"
            disabled={!pageUrl}
            onClick={() => void handleCopyPageUrl()}
          >
            <Copy className="h-4 w-4" />
          </Button>
          {pageUrl ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              title="Open page in new tab"
              asChild
            >
              <a
                href={pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open page in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              aria-label="Open page in new tab"
              title="Open page in new tab"
              disabled
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Cover art</Label>
        <div className="space-y-2">
          {coverArt && (
            <div className="relative aspect-square max-w-xs bg-gray-100 rounded-md overflow-hidden border border-gray-200">
              <img
                src={coverArt.url}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6"
                onClick={() => onCoverArtChange(null)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsCoverArtDialogOpen(true)}
            className="w-full max-w-xs"
          >
            <ImageIcon className="h-4 w-4 mr-2" />
            {coverArt ? "Replace cover art" : "Select cover art"}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Audio file</Label>
        <div className="space-y-2">
          {audioFile && (
            <div className="rounded-lg border p-4 space-y-3 max-w-md">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {audioFile.originalname || "Audio file"}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => onAudioFilesChange([])}
                  aria-label="Remove audio file"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <audio controls src={audioFile.url} className="w-full" />
            </div>
          )}
          {!audioFile && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAudioDialogOpen(true)}
              className="w-full max-w-xs"
            >
              <Music className="h-4 w-4 mr-2" />
              Select audio
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2 max-w-md">
        <Label htmlFor="product-isrc">ISRC</Label>
        <Input
          id="product-isrc"
          value={isrc}
          onChange={(e) => onIsrcChange(e.target.value)}
          placeholder="USRC17607839"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 max-w-md">
          <Label>Links</Label>
          {isrc.trim() ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isRefreshingLinks}
              onClick={() => void handleRefreshLinks()}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isRefreshingLinks ? "animate-spin" : ""}`}
              />
              Refresh from ISRC
            </Button>
          ) : null}
        </div>
        <div className="space-y-3 max-w-md">
          {links.map((link, index) => (
            <LinkEditor
              key={index}
              link={link}
              onChange={(updated) => updateLink(index, updated)}
              onRemove={() => removeLink(index)}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              onLinksChange([
                ...links,
                { icon: "", label: "", ctaText: "", url: "" },
              ])
            }
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add link
          </Button>
        </div>
      </div>

      <div className="space-y-4 max-w-md">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="buyDigitalEnabled">Buy digital button</Label>
            <p className="text-sm text-muted-foreground">
              Show a button that starts checkout for this track.
            </p>
          </div>
          <Switch
            id="buyDigitalEnabled"
            checked={buyDigitalEnabled}
            onCheckedChange={onBuyDigitalEnabledChange}
          />
        </div>
        {buyDigitalEnabled ? (
          <div className="space-y-1.5">
            <Label htmlFor="buyDigitalText">Button text</Label>
            <Input
              id="buyDigitalText"
              value={buyDigitalText}
              onChange={(e) => onBuyDigitalTextChange(e.target.value)}
              placeholder="Buy Digital"
            />
          </div>
        ) : null}
      </div>

      <ImageSelectDialog
        open={isCoverArtDialogOpen}
        onOpenChange={setIsCoverArtDialogOpen}
        onSelect={({ fileId, url }) => onCoverArtChange({ fileId, url })}
        title="Select Cover Art"
        description="Choose an image from your uploaded files or upload a new one"
      />

      <AudioSelectDialog
        open={isAudioDialogOpen}
        onOpenChange={setIsAudioDialogOpen}
        onSelect={({ fileId, url, originalname }) =>
          onAudioFilesChange([{ fileId, url, originalname }])
        }
        title="Select Audio File"
        description="Choose an audio file from your uploaded files or upload a new one"
      />
    </div>
  );
}
